import type es from './locales/es';
import { LANGUAGES, SOURCE_LANGUAGE, isLanguage, preferredLanguage, storeLanguage } from './languages';
import type { Language } from './languages';

export { LANGUAGES, detectLanguage, isLanguage, storedLanguage, toLanguage } from './languages';
export type { Language } from './languages';

/**
 * Translations by key. Each screen has its namespace (a JSON file per language in locales/<lang>/),
 * and a key reads "namespace.key": t('tables.title').
 *
 * - {name} placeholders are filled from vars: t('tables.occupied', { count: 3 }).
 * - Plurals: with vars.count, "key_one" / "key_other" (and "key_zero", "key_few"... where the
 *   language has them, by Intl.PluralRules) win over "key".
 * - A key missing in a language falls back to Spanish, never to the raw key.
 */
type Catalog = typeof es;
type Namespace = keyof Catalog;
type PluralSuffix = '_zero' | '_one' | '_two' | '_few' | '_many' | '_other';
type StripPlural<K extends string> = K extends `${infer Base}${PluralSuffix}` ? Base : K;
export type TranslationKey = {
  [N in Namespace]: `${N & string}.${StripPlural<keyof Catalog[N] & string>}`;
}[Namespace];
export type Vars = Record<string, string | number>;
type Messages = Record<string, Record<string, string>>;

let source: Messages = {};
let messages: Messages = source;
let language: Language = SOURCE_LANGUAGE;
let plurals = new Intl.PluralRules(SOURCE_LANGUAGE);

const loaders = import.meta.glob<{ default: Record<string, string> }>('./locales/*/*.json');

async function loadCatalog(code: Language, only?: string[]): Promise<Messages> {
  const prefix = `./locales/${code}/`;
  const entries = await Promise.all(
    Object.entries(loaders)
      .filter(([path]) => path.startsWith(prefix))
      .filter(([path]) => !only || only.includes(path.slice(prefix.length, -'.json'.length)))
      .map(async ([path, load]) => [path.slice(prefix.length, -'.json'.length), (await load()).default] as const),
  );
  return Object.fromEntries(entries);
}

/**
 * Loads the language before the app renders, so even module-level labels come out translated.
 * `only` limits it to some namespaces (the public pages don't need the app's texts).
 */
export async function setupI18n(code: Language = preferredLanguage(), only?: string[]): Promise<Language> {
  source = await loadCatalog(SOURCE_LANGUAGE, only);
  try {
    messages = code === SOURCE_LANGUAGE ? source : await loadCatalog(code, only);
    language = code;
  } catch {
    messages = source;
    language = SOURCE_LANGUAGE;
  }
  plurals = new Intl.PluralRules(LANGUAGES.find((item) => item.code === language)?.htmlLang ?? language);
  document.documentElement.lang = LANGUAGES.find((item) => item.code === language)?.htmlLang ?? language;
  return language;
}

export function currentLanguage(): Language {
  return language;
}

/** Saves the choice on this device and starts the screen over in the new language. */
export function switchLanguage(code: Language, reload = true): void {
  if (!isLanguage(code)) return;
  storeLanguage(code);
  if (!reload) return;
  // A ?lang= in the address would win again after the reload: the choice just made replaces it.
  const url = new URL(window.location.href);
  if (url.searchParams.has('lang')) {
    url.searchParams.delete('lang');
    window.history.replaceState(window.history.state, '', url);
    window.location.reload();
  } else if (code !== language) {
    window.location.reload();
  }
}

function lookup(table: Messages, namespace: string, key: string, count: number | undefined): string | undefined {
  const entries = table[namespace];
  if (!entries) return undefined;
  if (count !== undefined) {
    const exact = count === 0 ? entries[`${key}_zero`] : undefined;
    const form = exact ?? entries[`${key}_${plurals.select(count)}`] ?? entries[`${key}_other`];
    if (form !== undefined) return form;
  }
  return entries[key];
}

function fill(text: string, vars?: Vars): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (match, name: string) => (name in vars ? String(vars[name]) : match));
}

export function t(key: TranslationKey, vars?: Vars): string {
  const dot = key.indexOf('.');
  const namespace = key.slice(0, dot);
  const name = key.slice(dot + 1);
  const count = typeof vars?.count === 'number' ? vars.count : undefined;
  const text = lookup(messages, namespace, name, count) ?? lookup(source, namespace, name, count);
  if (text === undefined) {
    if (import.meta.env.DEV) console.warn(`[i18n] missing key ${key}`);
    return key;
  }
  return fill(text, vars);
}

const extra = new Map<Language, Messages>();

/**
 * Loads some namespaces of another language, for text that follows someone else's language:
 * the printed ticket speaks the restaurant's language whatever the cashier reads.
 */
export async function preloadLanguage(code: Language, only: string[]): Promise<void> {
  if (code === language) return;
  const loaded = await loadCatalog(code, only);
  extra.set(code, { ...(extra.get(code) ?? {}), ...loaded });
}

/** Like t(), in a given language (preloaded with preloadLanguage); falls back to Spanish. */
export function tIn(code: string | null | undefined, key: TranslationKey, vars?: Vars): string {
  if (!isLanguage(code) || code === language) return t(key, vars);
  const table = extra.get(code);
  if (!table) return t(key, vars);
  const dot = key.indexOf('.');
  const count = typeof vars?.count === 'number' ? vars.count : undefined;
  const rules = new Intl.PluralRules(LANGUAGES.find((item) => item.code === code)?.htmlLang ?? code);
  const entries = table[key.slice(0, dot)];
  const name = key.slice(dot + 1);
  let text: string | undefined;
  if (entries && count !== undefined) {
    text = (count === 0 ? entries[`${name}_zero`] : undefined) ?? entries[`${name}_${rules.select(count)}`] ?? entries[`${name}_other`];
  }
  text = text ?? entries?.[name];
  return text === undefined ? t(key, vars) : fill(text, vars);
}

let guest: Language = SOURCE_LANGUAGE;

/** The restaurant's language, for messages to its guests (WhatsApp texts the staff send). */
export function setGuestLanguage(code: string | null | undefined): void {
  if (!isLanguage(code)) return;
  guest = code;
  void preloadLanguage(code, ['orders', 'orderPanel', 'printedReceipt']).catch(() => undefined);
}

/** A text the restaurant sends its guests, in the restaurant's language. */
export function tGuest(key: TranslationKey, vars?: Vars): string {
  return tIn(guest, key, vars);
}

/** Locale for dates and lists in the person's language (amounts keep following the restaurant). */
export function dateLocale(): string {
  return LANGUAGES.find((item) => item.code === language)?.htmlLang ?? language;
}
